import { z } from 'zod';

/**
 * Where a piece of intelligence came from. Every stored observation carries one.
 *
 * - `torn_api`     — returned by the official Torn API (authoritative for what it covers).
 * - `extension`    — seen on a Torn page by a user's browser extension (observed, not confirmed).
 * - `user_report`  — typed in by a user (lowest default trust).
 * - `derived`      — computed by us from other observations (e.g. a battle-stat estimate).
 */
export const SourceType = z.enum(['torn_api', 'extension', 'user_report', 'derived']);
export type SourceType = z.infer<typeof SourceType>;

/**
 * How certain we are. Kept as a coarse label for UI; numeric scores can be added
 * per-observation later without changing this contract.
 */
export const Certainty = z.enum(['confirmed', 'observed', 'estimated']);
export type Certainty = z.infer<typeof Certainty>;

/**
 * Who may see an observation. Faction/private scopes are enforced server-side.
 * Format: `public` | `faction:<factionId>` | `private:<userId>`.
 */
export const Visibility = z
  .string()
  .regex(/^(public|faction:\d+|private:\d+)$/, 'invalid visibility scope');
export type Visibility = z.infer<typeof Visibility>;

export const Provenance = z.object({
  source: SourceType,
  certainty: Certainty,
  /** When the underlying fact was true/seen (unix seconds). */
  observedAt: z.number().int(),
  /** Seconds between observedAt and the moment the response was produced. */
  ageSeconds: z.number().int().nonnegative(),
  /** True when the data is older than the freshness threshold for its kind. */
  stale: z.boolean(),
});
export type Provenance = z.infer<typeof Provenance>;
